import { getHttp } from '@kate/plugins/http';
import { getLogger } from '@kate/plugins/logger';
import { TOKEN_LOGIN_PATH } from '@/constants.js';
import useGlobalStore from '@stores/global';

async function signInWithToken(token) {
  const logger = getLogger();

  // `?token` with no value or a repeated `token` isn't a link the app sent.
  if (typeof token !== 'string' || token === '') {
    logger.warn('Malformed jwt token', undefined, undefined, true);
    return false;
  }

  logger.info('Validating jwt token', undefined, true);
  try {
    await getHttp().post(
      TOKEN_LOGIN_PATH,
      {},
      { withCredentials: true, headers: { Authorization: `Bearer ${token}` } },
    );
    logger.info('Validated jwt token');
    return true;
  } catch (err) {
    logger.warn('Error validating jwt token', undefined, err);
    return false;
  }
}

async function getProfile() {
  const logger = getLogger();

  logger.info('Getting user profile info');
  try {
    const { data } = await getHttp().get('/api/profile/user');
    logger.info('Got user profile info');
    return data;
  } catch (err) {
    logger.warn('Failed to get user profile info', undefined, err);
    return null;
  }
}

// An existing session wins over the emailed link, so a link for another account can't silently
// swap a signed-in applicant into it.
async function loadSession(token) {
  const store = useGlobalStore();
  let profile = await getProfile();

  if (!profile && token !== undefined) {
    if (!(await signInWithToken(token))) {
      store.signInLinkError = 'invalid';
      return;
    }
    profile = await getProfile();
    if (!profile) {
      store.signInLinkError = 'failed';
      return;
    }
  }

  if (!profile) {
    return;
  }

  store.logIn(profile);
  await store.loadApplications();
}

export function createSessionGuard() {
  let sessionLoad;

  return async to => {
    // A public page other than login has nothing to read from the session, so the first visit
    // skips loading it. Login still loads it, for the emailed sign-in token and to send a
    // signed-in applicant away. Leaving the load unset means a later page that needs the session
    // still loads it. A route that forgets `meta.public` stays protected.
    if (to.meta.public && to.name !== 'login' && !sessionLoad) {
      return true;
    }

    // Only the navigation that loads the session reads the emailed link's token. Opening the link
    // loads the app on the login page, so that navigation is the one that loads it, and other
    // pages leave a `token` query alone.
    const loadsSession = !sessionLoad;
    const hasSignInToken = loadsSession && to.name === 'login' && to.query.token !== undefined;
    sessionLoad ??= loadSession(hasSignInToken ? to.query.token : undefined);
    await sessionLoad;

    // Whether it worked or not, so the token doesn't stay in the address bar or history.
    if (hasSignInToken) {
      const query = { ...to.query };
      delete query.token;
      return { path: to.path, query, hash: to.hash, replace: true };
    }

    const store = useGlobalStore();

    if (!store.isLoggedIn && !to.meta.public) {
      getLogger().info('Redirecting to login page', undefined, true);
      return { name: 'login', query: { next: encodeURIComponent(to.fullPath) } };
    }

    if (store.isLoggedIn && to.name === 'login') {
      getLogger().info('Redirecting to root', undefined, true);
      return { name: 'root' };
    }

    return true;
  };
}
